import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import queryString from 'query-string';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  deleteDraft,
  getDraftArray,
  isPublicForm,
  submitImages,
  submitForm,
  submitDraft,
  getAllDrafts,
} from 'apps/web_forms/api/api';
import Loading from 'apps/web_forms/components/commons/Loading';
import Modal from 'apps/web_forms/components/commons/Modal';
import DMButton from 'apps/web_forms/components/commons/DMButton';
import FormPageHeader from 'apps/web_forms/components/FormPageHeader';
import Notification from 'apps/web_forms/components/Notification';
import SaveDraftModal from 'apps/web_forms/components/SaveDraftModal';
import { useDraftImages } from 'apps/web_forms/hooks/useDraftImages';
import { useDraftsMetadata } from 'apps/web_forms/hooks/useDraftsMetadata';
import { useFormsMetadata } from 'apps/web_forms/hooks/useFormsMetadata';
import { useDispatchesListQuery } from 'apps/web_forms/hooks/useDispatchesListQuery';
import useLoadFormResources from 'apps/web_forms/hooks/useLoadFormResources';
import locationHook from 'apps/web_forms/hooks/useLocation';
import useXForm from 'apps/web_forms/hooks/useXForm';
import {
  setSuccessfulAction,
  showActionSuccessMessage,
  setSelectedForm,
  resetSelectedForm
} from 'apps/web_forms/slices/appSlice';
import {
  resetFormSliceState,
  setDraftAnswers,
  updateFieldsResourceUpdateHighlight,
  setResourceVersions,
} from 'apps/web_forms/slices/formSlice';
import {
  addImageDraftValues,
  resetMultimediaSliceState,
} from 'apps/web_forms/slices/multimediaSlice';
import {
  APP_ROUTES,
  COLORS,
  FEET_TO_METERS,
  dispatchTypes,
  ids,
  LEAVE_WARNING_MSG,
  modalMessages,
  ORIGINS,
  HIDE_DRAFTS,
} from 'apps/web_forms/utils/constants';
import { MultiDispatchError } from 'apps/web_forms/utils/errors';
import { getMeasureUnitFromString } from 'apps/web_forms/utils/inputsUtils';
import { getImageInputIdentifiers } from 'apps/web_forms/utils/images';
import {
  isWithinRadius,
  locationParse,
} from 'apps/web_forms/utils/locationHelpers';
import { NotificationBarContext } from 'apps/web_forms/contexts/NotificationBarContext';

import FormElements from './FormElements';
import IssuesFoundButton from './IssuesFoundButton';
import { StyledForm, StyledFormControl } from './styles';
import {
  buildSubmittedTree,
  getFieldsAffectedByUpdate,
  getInitialAnswers,
  parseXml,
} from './utils';
import { useBeforeunload } from 'react-beforeunload';
import useGlobalSelector, {
  selectIsLoading,
  selectFormList,
  selectDraftList,
} from 'apps/web_forms/hooks/useGlobalSelector';
import { FormTypes } from 'apps/web_forms/utils/constants';

// TODO: refactor to separate components/pages for Form, Draft, Dispatch (w/
// common component for shared behavior, of course). Also generally just
// refactor into smaller pieces, this file is a monster.
const FormOld = () => {
  const formMethods = useForm();
  const { search } = useLocation();
  const params = queryString.parse(search);
  const isLoading = useSelector(selectIsLoading);
  const formList = useSelector(selectFormList);
  const draftList = useSelector(selectDraftList);
  const [formVersion, setFormVersion] = useState();

  const {
    imagesToSubmit,
    imageDraftValues,
    fieldErrors,
    areProcessesRunning,
    requiredResourcesLoading,
    draftAnswers,
    repeatGroupFields,
    dispatchAnswers,
    rootLevelFields,
  } = useGlobalSelector();

  const formContext = useRef({
    formState: formMethods.formState,
    register: formMethods.register,
    unregister: formMethods.unregister,
    setValue: formMethods.setValue,
    getValues: formMethods.getValues,
    setError: formMethods.setError,
    trigger: formMethods.trigger,
    clearErrors: formMethods.clearErrors,
  });
  const { id: authenticatedFormId, submissionId: submissionId } = useParams();
  const id = !isPublicForm() ? authenticatedFormId : window.sharedFormFormID;

  const dispatch = useDispatch();

  let selectedForm = {};
  let type = params.origin ?? ORIGINS.Form;

  const isDraft = params.origin === ORIGINS.Draft;
  const draftId = isDraft ? params.id : undefined;

  let drafts = draftList.data;
  let forms = formList.data;

  // selectedForm is not empty when opening a form, draft, dispatch, submission by clicking a form element.
  // selectedForm is not empty when reloading a form, draft, dispatch, submission after having opened it by clicking on a form element.
  // selectedForm is not empty when opening a shared form.
  // selectedForm is not empty when reloading a shared form
  // selectedForm is not empty when opening the url of a single form, draft, dispatch, submission and shared form manually.
  const setSelectedFormFn = useCallback((forms, type) => {
    const currentForm = forms.find((form) => {
      switch (type) {
        case 'form':
        case 'dispatch':
        case 'submission':
          return parseInt(form.identifier) === parseInt(id);
        case 'draft':
          return parseInt(form.identifier) === parseInt(draftId);
        default:
          return parseInt(form.identifier) === parseInt(id);
      }
    });
    let formVersionValue = currentForm?.version;
    if (type === 'draft') {
      formVersionValue = currentForm?.version;
    }
    if (formVersionValue) {
      setFormVersion(formVersionValue);
      dispatch(setSelectedForm(currentForm));
    }
  }, [id, draftId, setFormVersion, dispatch]);

  let fetchedForms = null;
  switch(type) {
    case 'form':
    case 'submission':
      const { forms } = useFormsMetadata();
      fetchedForms = forms;
      break;
    case 'draft':
      const { drafts } = useDraftsMetadata();
      fetchedForms = drafts;
      break;
    case 'dispatch':
      const { dispatches } = useDispatchesListQuery();
      fetchedForms = dispatches;
      break;
    default:
      break;
  }

  useEffect(() => {
    if (fetchedForms && fetchedForms.length > 0) {
      setSelectedFormFn(fetchedForms, type);
    }
  }, [type, fetchedForms, setSelectedFormFn]);
  
  const draftMetadata = useMemo(() => drafts?.find((draft) => draft.id === draftId), [drafts]);
  const draftDescription = draftMetadata?.description;

  const formMetadata = useMemo(() => forms?.find((formWhere) => formWhere.identifier === id), [forms]);
  const formDescription = formMetadata?.description;

  const isDispatchDraft = isDraft && draftMetadata?.isDispatch;
  const isSubmission = window.location.href.includes('submission');
  const isDispatch = window.location.href.includes('origin=dispatch');

  const { form, error, loading } = useXForm({
    formVersion: formVersion,
    formId: id,
    submissionId: submissionId,
  });

  const formRef = useRef(null);
  const defaultValues = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const history = useHistory();
  const [notificationBox, setNotificationBox] = useState(null);
  const {
    setShowNotificationBar,
    setNotificationBarText,
    setNotificationBarType,
    setAlignLeft,
    setButtons,
    setRightContents,
    showNotificationBar,
  } = useContext(NotificationBarContext);
  const [resourcesLoadingModalOpen, setResourcesLoadingModalOpen] =
    useState(false);
  const [submitFailModalOpen, setSubmitFailModalOpen] = useState(false);
  const [rangeFailureRadius, setRangeFailureRadius] = useState({
    value: '',
    unit: '',
  });
  const [rangeFailureModalOpen, setRangeFailureModalOpen] = useState(false);
  const [locationFailureModalOpen, setLocationFailureModalOpen] =
    useState(false);
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [multiDispatchFailModalOpen, setMultiDispatchFailModalOpen] =
    useState(false);
  const [isRenderedOnce, setIsRenderedOnce] = useState(false);
  const isDevelopment = process.env.NODE_ENV === 'development';

  const { getUserAccuracy } = locationHook();
  
  // Create the object to pass to the nested component
  const formType = useMemo(() => {
    return {
      isDraft,
      isDispatch,
      isDispatchDraft,
    };
  }, [isDraft, draftMetadata]);

  const {
    getResourceVersionsToSaveDraft,
    anyNeedUpdate,
    resourceUpdatesNeeded,
  } = useLoadFormResources(form, isDraft, draftMetadata);
  const [updateResourceConfirmationOpen, setUpdateResourceConfirmationOpen] =
    useState(false);

  const clearNotificationBar = () => {
    // clear notification bar
    setShowNotificationBar(false);
    setAlignLeft(true);
    setNotificationBarText('');
    setNotificationBarType('info');
    setButtons([]);
    setRightContents(undefined);
  };

  // For cleaning any global (i.e. redux) state that needs it before leaving form page
  const cleanup = () => {
    clearNotificationBar();
  };

  useBeforeunload(() => {
    // Before navigating away from page by any means
    cleanup();
  });

  const [numAffectedFields, setNumAffectedFields] = useState();

  const setResourcesHaveBeenUpdatedNotification = () => {
    setShowNotificationBar(true);
    setAlignLeft(true);
    setNotificationBarText('Resource(s) Successfully Updated');
    setNotificationBarType('info');
    setRightContents(<IssuesFoundButton />);
  };

  const updateResources = useCallback(() => {
    // Move some or all of this to useLoadFormResources?
    const newResourceVersions = {};
    resourceUpdatesNeeded.forEach((versions, resourceIdentifierHash) => {
      const { latestVersion } = versions;
      newResourceVersions[resourceIdentifierHash] = latestVersion;
    });
    dispatch(setResourceVersions(newResourceVersions));

    const updatedResources = Array.from(resourceUpdatesNeeded.keys());
    const fieldsAffectedByUpdate = getFieldsAffectedByUpdate(
      updatedResources,
      form.inputList,
      repeatGroupFields || {}
    );

    dispatch(
      updateFieldsResourceUpdateHighlight({
        fieldsAffectedByUpdate,
        isHighlighted: true,
      })
    );

    setUpdateResourceConfirmationOpen(false);
    clearNotificationBar();
    setResourcesHaveBeenUpdatedNotification();
  }, [resourceUpdatesNeeded, form.inputList, repeatGroupFields]);

  useEffect(() => {
    if (Object.keys(dispatchAnswers).length > 0 && !isRenderedOnce) {
      for (const [key, value] of Object.entries(dispatchAnswers)) {
        formMethods.register(key);
        formMethods.setValue(key, value);
      }
      setIsRenderedOnce(true);
    }
  }, [dispatchAnswers]);

  useEffect(
    function setResourceUpdateNotification() {
      if (anyNeedUpdate) {
        const updatedResources = Array.from(resourceUpdatesNeeded.keys());
        const fieldsAffectedByUpdate = getFieldsAffectedByUpdate(
          updatedResources,
          form.inputList,
          repeatGroupFields || {}
        );
        setNumAffectedFields(fieldsAffectedByUpdate.length);

        setShowNotificationBar(true);
        setAlignLeft(true);
        setNotificationBarText('New Resource Update Available');
        setNotificationBarType('info');
        setButtons([
          {
            id: ids.FormIds.UpdateResourcesButton,
            label: 'Update Resource(s)',
            onClick: () => {
              setUpdateResourceConfirmationOpen(true);
            },
            filled: true,
          },
        ]);
      }
    },
    [anyNeedUpdate, resourceUpdatesNeeded, repeatGroupFields]
  );

  const { draftImages, loading: loadingDraftImages } = useDraftImages({
    draftId,
    isDraft,
  });

  useEffect(() => {
    const inputs = form.inputList;
    if (isDraft && inputs) {
      const imageMap = new Map();
      draftImages.forEach((draftImage) => {
        /** Adding an if else check  for DEVELOPMENT environment. We will have a base64-data on development.
        For production, the value received from BE will be S3 URL, so it will be image-url*/
        if (draftImage.hasOwnProperty('base64-data')) {
          imageMap.set(
            draftImage['image-identifier'],
            `data:image/jpeg;base64,` + draftImage['base64-data']
          );
        } else {
          imageMap.set(draftImage['image-identifier'], draftImage['image-url']);
        }
      });

      let imageInputIdentifiers = [];
      /** Code adapted to work for images inside GROUP types. It checks for children then it gets the corresponding values */
      let cloneImageInputIdentifiers = getImageInputIdentifiers(inputs);
      if (repeatGroupFields) {
        cloneImageInputIdentifiers.map((imageInputIdentifier) => {
          if (Array.isArray(repeatGroupFields[imageInputIdentifier])) {
            repeatGroupFields[imageInputIdentifier].map((childIdentifier) => {
              imageInputIdentifiers.push(childIdentifier);
            });
          } else {
            imageInputIdentifiers.push(imageInputIdentifier);
          }
        });
      }

      let imageDrafts = {};
      imageInputIdentifiers.forEach((imageInputIdentifier) => {
        const imageIdentifier = draftAnswers[imageInputIdentifier];
        const imageUrl = imageMap.get(imageIdentifier);
        if (imageIdentifier && imageUrl) {
          imageDrafts[imageInputIdentifier] = { imageIdentifier, imageUrl };
        }
      });
      if (Object.entries(imageDrafts).length !== 0) {
        dispatch(addImageDraftValues({ imageDrafts }));
      }
    }
  }, [draftImages, draftAnswers, isDraft, form.inputList, repeatGroupFields]);

  const goBackUrl = useMemo(() => {
    switch (params.origin) {
      case ORIGINS.Dispatch:
        return APP_ROUTES.dispatches;
      case ORIGINS.Submission:
        return APP_ROUTES.webstorePage(id);
      case ORIGINS.Geolocated:
      case ORIGINS.Geofenced:
        return APP_ROUTES.getDispatchesDetails(params.origin);
      case ORIGINS.Draft:
        return APP_ROUTES.draftsPage;
      default:
        return APP_ROUTES.mainPage;
    }
  }, [search]);

  useEffect(
    function loadDefaultValues() {
      if (!form.inputList) return;
      if (defaultValues.current === null) {
        defaultValues.current = getInitialAnswers(form);
      }
    },
    [form]
  );

  useEffect(() => {
    const getDraftArrayFunc = async () => {
      if (isDraft && Object.keys(form).length > 0) {
        const fullDraft = await getDraftArray(draftId, form);
        const draftAnswerKeys = Object.keys(fullDraft?.draftInitialsAnswers);
        if (draftAnswerKeys) {
          draftAnswerKeys.forEach((key) => {
            if (!Array.isArray(fullDraft?.draftInitialsAnswers[key])) {
              formMethods.register(key);
              formMethods.setValue(key, fullDraft?.draftInitialsAnswers[key]);
            }
          });
        }
        dispatch(setDraftAnswers(fullDraft?.draftInitialsAnswers));
      }
    };

    getDraftArrayFunc();
  }, [form]);

  useEffect(() => {
    if (error.error) {
      cleanup();
      history.push(APP_ROUTES.getErrorPageByCode(error.code));
    }
  }, [error]);

  useEffect(() => {
    // Enable confirm request when the user tries to close the tab or window. Not apply on develop enviroment
    const beforeUnloadHandler = (e) => {
      e.returnValue = LEAVE_WARNING_MSG; // eslint-disable-line no-param-reassign
      return LEAVE_WARNING_MSG;
    };

    if (!isDevelopment)
      window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      if (beforeUnloadHandler) {
        // Remove confirm request when user closes the tab or window
        window.removeEventListener('beforeunload', beforeUnloadHandler);
      }
    };
  }, []);

  useEffect(() => {
    dispatch(setSelectedForm(selectedForm));
    // Triggered when the user leaves the form
    return () => {
      // Clean state related to the form when the user leaves it
      dispatch(resetFormSliceState());
      dispatch(resetMultimediaSliceState());

      // If this is false, a Redux middleware will block all actions trying to modify the form state
      dispatch(resetSelectedForm());
    };
  }, []);

  const handleSubmit = () => {
    if (Object.keys(fieldErrors).length) {
      onInvalidSubmit();
    } else {
      onValidSubmit();
    }
  };

  // Upon beginning to submit a valid form(/dispatch/draft)
  const onValidSubmit = () => {
    if (requiredResourcesLoading) {
      setResourcesLoadingModalOpen(true);
      return;
    }

    if (!formRef.current) return;
    if (!formRef.current.checkValidity()) return;

    setSubmitting(true);
  };

  const deleteDrafts = async (draftsToDelete) => {
    const deleteResponsePromises = draftsToDelete.map((draftToDelete) =>
      deleteDraft(draftToDelete.id)
    );
    const deletesPromise = Promise.all(deleteResponsePromises);
    await deletesPromise;
  };

  // Upon successful completion of the submission itself
  const onSuccesfulSubmit = async (type) => {
    const savingDraft = type === 'draft';
    if (!savingDraft) {
      // It was an actual form submission (whether from a normal form, dispatch, or draft), not saving/updating a draft
      if (isDraft) {
        // If we submitted a draft, delete it
        try {
          await deleteDraft(draftId);
        } catch (e) {
          if (isDispatchDraft) {
            // eslint-disable-next-line no-console
            console.error(e);
            // eslint-disable-next-line no-console
            console.log(`you can't submit a dispatch draft twice`);
            throw new MultiDispatchError(
              `Cause of MultiDispatchError:\n ${e.message}`
            );
          } else {
            throw e;
          }
        }
        if (isDispatchDraft) {
          // Dispatches can only be submitted once, so delete any other drafts of the same dispatch
          const draftsList = await getAllDrafts();
          const draftsOfDispatch = draftsList.filter(
            (otherDraft) =>
              otherDraft.form_id === parseInt(id) && otherDraft.id !== parseInt(draftId)
          );
          await deleteDrafts(draftsOfDispatch);
        }
      }

      if (isDispatch) {
        // Dispatches can only be submitted once, so delete any drafts of this dispatch
        const draftsList = await getAllDrafts();
        const draftsOfDispatch = draftsList.filter((draft) => draft.form_id === parseInt(id));
        await deleteDrafts(draftsOfDispatch);
      }
    }
  };

  const onInvalidSubmit = () => {
    setSubmitFailModalOpen(true);
  };

  const canSendGeoFencedForm = async () => {
    let allowSendForm = true;
    if (form.formType === dispatchTypes.GEO_FENCED) {
      try {
        const { latitude, longitude } = await getUserAccuracy();
        const radius = getMeasureUnitFromString(form?.geoFenceRadius);
        allowSendForm = isWithinRadius(
          { latitude, longitude },
          locationParse(form.location),
          radius.unit === 'ft' ? radius.value / FEET_TO_METERS : radius.value
        );

        if (!allowSendForm) {
          setRangeFailureRadius(radius);
          setRangeFailureModalOpen(true);
          setSubmitting(false);
        }
      } catch (e) {
        setLocationFailureModalOpen(true);
        setSubmitting(false);
        allowSendForm = false;
      }
    }
    return allowSendForm;
  };

  // Submits the form if there aren't any required resource loading or any running process
  useEffect(() => {
    async function onSubmitting() {
      if (submitting) {
        if (!areProcessesRunning) {
          if (await canSendGeoFencedForm()) {
            submit();
          }
        }
      }
    }
    onSubmitting();
  }, [submitting, areProcessesRunning]);

  // TODO: refactor this, along with Form.js as a whole
  const submit = async (type = 'form', description = null) => {
    const submitData = formMethods.getValues();

    Object.entries(submitData).forEach(([key, value]) => {
      if (value === undefined || value === null) submitData[key] = '';
    });

    if (Object.keys(imageDraftValues).length > 0) {
      Object.keys(imageDraftValues).forEach((fieldId) => {
        const imageProperties = {
          type: 'image',
          imageIdentifier: imageDraftValues[fieldId].imageIdentifier,
        };
        submitData[fieldId] = imageProperties;
      });
    }

    if (Object.keys(imagesToSubmit).length > 0) {
      const imagesUUIDValues = await submitImages(imagesToSubmit);
      if (imagesUUIDValues.error) {
        setNotificationBox({
          text: 'There was a problem during the image uploading',
          type: 'error',
        });
        setTimeout(() => {
          setNotificationBox(null);
        }, 3000);

        return null;
      }
      Object.keys(imagesUUIDValues).forEach((fieldId) => {
        const imageProperties = {
          type: 'image',
          imageIdentifier: imagesUUIDValues[fieldId],
        };
        submitData[fieldId] = imageProperties;
      });
    }

    if (type === 'form') {
      // DME-2913 remove values from inactive fields from submitData

      for (const [key, value] of Object.entries(rootLevelFields)) {
        const { isActive } = value;
        if (!isActive) {
          delete submitData[key];
        }
      }
    }

    const submittedTree = buildSubmittedTree(submitData);

    const finalXMLString = parseXml(submittedTree);

    const isUpdateExisting = type === 'draft' && isDraft;

    try {
      const formOptions = {
        finalXMLString,
        formVersion,
        formNamespace: form.namespace,
        submissionId: submissionId,
      };
      const draftOptions = {
        ...formOptions,
        description,
        isUpdateExisting,
        draftId: isUpdateExisting && draftId,
        resourceVersions: getResourceVersionsToSaveDraft(),
      };
      const response =
        type === 'form'
          ? await submitForm(formOptions)
          : await submitDraft(draftOptions);
      if (response?.status >= 200 && response?.status < 300) {
        await onSuccesfulSubmit(type);
        // onSuccesfulSubmit errors if any of its http calls fail, so if we get here everything succeeded
        if (!isPublicForm()) {
          cleanup();
          if (params.origin === ORIGINS.Submission) {
            history.push(APP_ROUTES.submissionSuccessPage); // push the blank success page onto the history to clear the form for the redirect
            window.location.replace(goBackUrl);
          } else {
            history.push(goBackUrl);
          }
        }
        if (type === 'draft') {
          dispatch(setSuccessfulAction('saved'));
        }
        dispatch(showActionSuccessMessage(true));
      }
    } catch (e) {
      if (e instanceof MultiDispatchError) {
        setMultiDispatchFailModalOpen(true);
      } else {
        // TODO: Add bugsnag
        // eslint-disable-next-line no-console
        console.log('There was an error submitting or saving the form', e);
        cleanup();
        history.push(APP_ROUTES.getErrorPageByCode(500));
      }
    } finally {
      setSubmitting(false);
    }
    return true;
  };

  // Prevent form submit on press enter
  const filterEnterKey = (e) => {
    if (e.keyCode !== 13) return;
    if (e.target.type === 'textarea') return;
    if (e.target.getAttribute('allow-enter') === 'true') return;

    e.preventDefault();
  };

  const [draftDescriptionInputValue, setDraftDescriptionInputValue] =
    useState(draftDescription);
  const openDraftModal = () => {
    setDraftModalOpen(true);
    setDraftDescriptionInputValue(draftDescription);
  };

  const returnToWebStore = () => {
    window.location.replace(goBackUrl);
  };

  const close = () => {
    setDraftModalOpen(false);
  };
  const saveDraft = () => {
    submit('draft', draftDescriptionInputValue);
    close();
  };

  return (
    <>
      {resourcesLoadingModalOpen && (
        <Modal
          mainMessage={'There are required resources loading'}
          helpTip={'Please wait'}
          isOpen={resourcesLoadingModalOpen}
          setIsOpen={setResourcesLoadingModalOpen}
          icon='error'
        />
      )}
      {submitFailModalOpen && (
        <Modal
          mainMessage={modalMessages.submitFail.main}
          helpTip={modalMessages.submitFail.tip}
          isOpen={submitFailModalOpen}
          setIsOpen={setSubmitFailModalOpen}
          icon='error'
          testId={ids.FormIds.FormSubmitFailModal}
          children={
            Object.keys(fieldErrors).length === 1 ? (
              <p className='error-field'>{Object.keys(fieldErrors)[0]}</p>
            ) : (
              <p className='error-field'>
                {Object.keys(fieldErrors)[0]} and{' '}
                {Object.keys(fieldErrors).length - 1} other errors.
              </p>
            )
          }
        />
      )}
      {rangeFailureModalOpen && (
        <Modal
          mainMessage={`You must be within the range of ${rangeFailureRadius.value} ${rangeFailureRadius.unit} to send this form`}
          helpTip={'Please get closer to the range and try again'}
          isOpen={rangeFailureModalOpen}
          setIsOpen={setRangeFailureModalOpen}
          icon='error'
        />
      )}
      {locationFailureModalOpen && (
        <Modal
          mainMessage={'Failed getting user location'}
          helpTip={
            "To send a geofenced form it's necessary to allow browser location first"
          }
          isOpen={locationFailureModalOpen}
          setIsOpen={setLocationFailureModalOpen}
          icon='error'
        />
      )}
      {multiDispatchFailModalOpen && (
        <Modal
          mainMessage={modalMessages.multiDispatchFail.main}
          helpTip={modalMessages.multiDispatchFail.tip}
          isOpen={multiDispatchFailModalOpen}
          setIsOpen={setMultiDispatchFailModalOpen}
          icon='error'
          testId={ids.FormIds.MultiDispatchFailModal}
          buttons={[
            {
              id: ids.FormIds.MultiDispatchFailModalOkButton,
              label: 'Ok',
              onClick: () => {
                cleanup();
                history.push(goBackUrl);
              },
              filled: true,
            },
          ]}
        />
      )}
      {draftModalOpen && (
        <SaveDraftModal
          draftDescriptionInputValue={draftDescriptionInputValue}
          setDraftDescriptionInputValue={setDraftDescriptionInputValue}
          draftModalOpen={draftModalOpen}
          setDraftModalOpen={setDraftModalOpen}
          mainMessage={modalMessages.saveDraft.main}
          helpTip={modalMessages.saveDraft.tip}
          saveButtonText='Save'
          onSave={saveDraft}
        />
      )}
      {updateResourceConfirmationOpen && (
        <Modal
          mainMessage={'Are you sure you want to update?'}
          helpTip={
            <>
              By updating the resource <b>{numAffectedFields} QUESTIONS</b> will
              be impacted. After an update, you will be guided through reviewing
              questions that have changed.
            </>
          }
          isOpen={updateResourceConfirmationOpen}
          setIsOpen={setUpdateResourceConfirmationOpen}
          buttons={[
            {
              id: ids.FormIds.UpdateResourceConfirmationButton,
              label: 'Update',
              onClick: updateResources,
              filled: true,
            },
            {
              id: ids.FormIds.UpdateResourceCancelButton,
              label: 'Cancel',
              onClick: () => setUpdateResourceConfirmationOpen(false),
              filled: false,
              materialText: true,
              mainColor: COLORS.red,
            },
          ]}
        />
      )}
      {notificationBox && (
        <Notification
          notificationText={notificationBox.text}
          type={notificationBox.type}
        />
      )}
      {(loading || isLoading) && <Loading />}

      {submitting && <Loading />}

      {loadingDraftImages && <Loading />}

      <Prompt
        message={(location, action) => {
          if (action === 'POP') {
            return LEAVE_WARNING_MSG;
          }
          return true;
        }}
      />

      <StyledForm
        hide={isLoading || loading}
        className={`form-container`}
        onKeyDown={filterEnterKey}
        onSubmit={formMethods.handleSubmit(handleSubmit, handleSubmit)}
        ref={formRef}
        data-testid='form'
      >
        <FormPageHeader
          sticky
          notificationBarShowing={showNotificationBar}
          submissionId={submissionId}
          isDraft={isDraft}
          title={form?.title}
          description={formDescription}
          draftDescription={draftDescription}
          goBackUrl={goBackUrl}
          isSubmitting={submitting || loadingDraftImages}
          renderToggleButton={false}
          renderRightButton={() => (
            <DMButton
              disabled={submitting || loadingDraftImages}
              filled
              type='submit'
            >
              {isSubmission ? 'Save' : 'Submit'}
            </DMButton>
          )}
          renderSecondaryRightButton={
            !HIDE_DRAFTS && !isPublicForm() && !isSubmission
              ? () => (
                  <DMButton
                    disabled={submitting || loadingDraftImages}
                    callbackMethod={() => openDraftModal()}
                  >
                    {!isDraft ? 'Save As Draft' : 'Update Draft'}
                  </DMButton>
                )
              : isSubmission
              ? () => (
                  <DMButton
                    disabled={submitting || loadingDraftImages}
                    callbackMethod={() => returnToWebStore()}
                  >
                    {'Cancel'}
                  </DMButton>
                )
              : null
          }
        />

        {form.inputList && (
          <StyledFormControl>
            <FormElements
              elements={form.inputList}
              getInput={form.getInput}
              dispatchInitialAnswers={form.dispatchInitialAnswers}
              formContext={formContext.current}
              formErrors={formMethods.errors}
              formType={formType}
            />
          </StyledFormControl>
        )}
      </StyledForm>
    </>
  );
};

export default FormOld;
